import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { urlParameterCodes } from '@/constants/urlParams';
import { ParsedProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedProductVariant';
import { SelectedProductVariants } from '@/ui/components/pages/ProductDetail/types/SelectedProductVariants';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { Bundle } from '@/ui/services/BundleService';
import { getOffDefaultSkus } from '@/ui/services/utils/bundle-links';
import { snapSelectionToCountry } from '@/ui/services/utils/bundle-country-snap';
import { getUrlParamValue, updateUrlParam } from '@/ui/utils/client-url-helpers';
import { formatBundleSkusParam, parseBundleSkusParam } from '@/utils/bundle-helper';

const bundleSkusParam = urlParameterCodes.productDetail.bundleSkus;

export interface BundleVariantSelection {
  selectedVariants: SelectedProductVariants;
  selectVariant: (bundleProductInstanceId: string, variant: ParsedProductVariant) => void;
  resetForBundle: (bundle: Bundle) => void;
}

interface SelectionState {
  // Identifies the bundle the variants were last seeded or reset for; null until then.
  appliedBundleKey: string | null;
  variants: SelectedProductVariants;
}

// Callers re-create bundle instances on re-render, so identity is compared by content id.
const getBundleKey = (bundle: Bundle): string => bundle.id || bundle.documentExtRef || '';

// The URL carries the same SKUs as a link to the selection, so a shared URL and a link agree.
const getUrlSkus = (
  bundle: Bundle,
  variants: SelectedProductVariants,
  hostProductId?: string,
): string | undefined =>
  formatBundleSkusParam(
    getOffDefaultSkus(
      bundle.products.filter((product) => product.id !== hostProductId),
      variants,
    ),
  );

// Nothing to store when every slot already holds the same variant for the same bundle;
// keeping the previous state spares a render and keeps the selection's identity stable.
const isSameSelection = (
  previous: SelectionState,
  bundleKey: string,
  variants: SelectedProductVariants,
): boolean => {
  const instanceIds = Object.keys(variants);
  return (
    previous.appliedBundleKey === bundleKey &&
    instanceIds.length === Object.keys(previous.variants).length &&
    instanceIds.every((instanceId) => previous.variants[instanceId] === variants[instanceId])
  );
};

const writeBundleSkusParam = (value: string | undefined) => {
  if ((getUrlParamValue(bundleSkusParam) ?? undefined) === value) {
    return;
  }

  updateUrlParam(bundleSkusParam, value);
};

/**
 * Holds the variant chosen for every slot of a bundle, keeps it on what the country sells
 * and mirrors the choices of the configurable slots to the URL, so a shared link restores
 * them. The host product's slot is left out of the URL because the host keeps its own `sku`
 * param. A bundle given at mount is seeded from the request's search params in the first
 * render, so a deep link paints its own set right away; a bundle that arrives later is
 * seeded by `resetForBundle`.
 */
export function useBundleVariantSelection(
  bundle: Bundle | undefined,
  options: { hostProductId?: string } = {},
): BundleVariantSelection {
  const { hostProductId } = options;
  const { country } = useLocalization();
  const searchParams = useSearchParams();
  // Seeding in the initializer instead of an effect lets the first paint show the deep-linked
  // set: no flash of the default set and no gallery remount when the effect would switch it.
  // The page renders per request, so the server and the client read the same params and agree.
  // Without a bundle at mount (the chooser picks one in an effect), or without params outside
  // the app router, the reset seeds it later: defaults seeded here would let the URL mirroring
  // drop a deep link before the reset reads it.
  const [selection, setSelection] = useState<SelectionState>(() => {
    if (!bundle || !searchParams) {
      return { appliedBundleKey: null, variants: {} };
    }

    const urlSkus = parseBundleSkusParam(searchParams.get(bundleSkusParam));
    return {
      appliedBundleKey: getBundleKey(bundle),
      variants: snapSelectionToCountry(
        bundle.products,
        bundle.selectVariantsBySKUs(urlSkus),
        country,
        { skipProductId: hostProductId },
      ),
    };
  });
  // The parameter is only ours to remove once this hook has written it for a bundle;
  // a render without a bundle before that (or a re-run effect) must keep a deep link.
  const hasWrittenParamRef = React.useRef(false);

  const selectVariant = useCallback(
    (bundleProductInstanceId: string, variant: ParsedProductVariant) => {
      setSelection((previous) => ({
        ...previous,
        variants: { ...previous.variants, [bundleProductInstanceId]: variant },
      }));
    },
    [],
  );

  // One variant per slot of the given bundle: a slot keeps the variant chosen
  // before when that variant is purchasable in it, then the URL decides, then the slot's
  // default variant. Variants of products outside the bundle are dropped. After a seed at
  // mount every slot is kept, so the first reset leaves the seeded selection as it is.
  const resetForBundle = useCallback((nextBundle: Bundle) => {
    const urlSkus = parseBundleSkusParam(getUrlParamValue(bundleSkusParam));
    const seeded = nextBundle.selectVariantsBySKUs(urlSkus);

    setSelection((previousSelection) => {
      const previous = previousSelection.variants;
      const next: SelectedProductVariants = {};
      nextBundle.products.forEach((product) => {
        const instanceId = product.bundleProductInstanceId;
        const kept =
          previous[instanceId] && product.getPurchasableVariantBySKU(previous[instanceId].sku);
        next[instanceId] = kept || seeded[instanceId];
      });
      const nextKey = getBundleKey(nextBundle);
      return isSameSelection(previousSelection, nextKey, next)
        ? previousSelection
        : { appliedBundleKey: nextKey, variants: next };
    });
  }, []);

  const { appliedBundleKey, variants: selectedVariants } = selection;
  const hasBundle = !!bundle;
  const bundleKey = bundle ? getBundleKey(bundle) : undefined;
  // Until the selection has been seeded or reset for this bundle it does not reflect the
  // URL seed yet, and writing it would overwrite a deep link.
  const isApplied = hasBundle && appliedBundleKey === bundleKey;
  const urlSkus =
    bundle && isApplied ? getUrlSkus(bundle, selectedVariants, hostProductId) : undefined;

  // A slot left on a variant the country hides moves to its sole visible option. The host's
  // slot is left to the host's own pickers and their country snap. The snap gives nothing for
  // a variant that is already the sole visible one, so the update cannot loop.
  useEffect(() => {
    if (!bundle || !isApplied) {
      return;
    }

    const snapOptions = { skipProductId: hostProductId };
    const snap = (variants: SelectedProductVariants) =>
      snapSelectionToCountry(bundle.products, variants, country, snapOptions);
    if (snap(selectedVariants) !== selectedVariants) {
      setSelection((previous) => ({ ...previous, variants: snap(previous.variants) }));
    }
  }, [bundle, isApplied, selectedVariants, country, hostProductId]);

  // Depends on strings only and skips unchanged values: callers re-create the bundle on
  // every render, and an unconditional replaceState cancels an in-flight router.push
  // (e.g. the checkout navigation from the cart drawer).
  useEffect(() => {
    if (!hasBundle) {
      if (hasWrittenParamRef.current) {
        hasWrittenParamRef.current = false;
        writeBundleSkusParam(undefined);
      }

      return;
    }

    if (!isApplied) {
      return;
    }

    hasWrittenParamRef.current = true;
    writeBundleSkusParam(urlSkus);
  }, [hasBundle, bundleKey, isApplied, urlSkus]);

  return { selectedVariants, selectVariant, resetForBundle };
}
